// eslint-disable-next-line import/no-cycle
import bffApi, {BffResponse} from '@client/bffApi';
import {
  CustodianService,
  BankAccountIdentityStatus,
  VerifyDepositTransferResponse,
  CancelMicroDepositOptions,
  DelayedTransfersOptions,
  DelayedTransfer,
  CreateClBankAccountOptions,
  UpdateRequestedTransferRequest,
  UpdateRequestedTransferResponse,
  PlaidItemUpdate,
  NextBusinessDateOptions,
  BusinessDateResponse,
  GetCustodianTransferFrequencyOptions,
  TransferFrequencyObject,
  VerifyMicroDepositOptions,
  GetCustodianAccountsOptions,
  LinkBankAccountOptions,
  GetMicroDepositOptions,
  GetPlaidAccountOptions,
  GetPlaidAccountResponse,
  GetAccessTokenRequest,
  GetAccessTokenResponse,
  UpdateTransferMoneyRequestOptions,
  TransferMoneyRequestOptions,
  GetAllBankAccountLinkRequestsOptions,
  BankAccountLinkRequest,
  LinkedCustodianAccount,
  GetLinkedBankAccountsForClUserOptions,
  DeleteMoneyTransferRequestOptions,
  PatchBankAccountLinkRequestOptions,
  VerifyDepositOptions,
  PostTransactionsOptions,
  TransactionReportResponseOptions,
} from '@simpleinvest/ms-custodian-sdk';
// eslint-disable-next-line import/no-cycle
import {apiKey, getLogger} from '@client/services/sdk';
// eslint-disable-next-line import/no-cycle
import {
  requestInterceptorFulfilledHandler,
  requestInterceptorRejectedHandler,
  responseInterceptorFulfilledHandler,
  responseInterceptorRejectedHandler,
} from '@client/interceptors';
import {accountType as AccountType} from '@client/enums/accountType';
import {AccountTransactionDetails, ClientService} from '@simpleinvest/ms-client-sdk';
import {TRANSACTIONS} from '@client/util/constants';
import {MicroserviceResponse, SourcePlatform} from '@simpleinvest/ms-core-sdk';
import {ApexService, ApexLabelResponse} from '@simpleinvest/ms-apex-sdk';
import {AxiosResponse} from 'axios';
import {TransactionValidationResponse} from './response/ServiceResponse';

export interface PlaidLinkTokenRequest {
  clUserId: number;
  clientName: string;
  webhookUrl: string;
  redirectUri: string;
  itemId: string;
}

export interface PlaidLinkTokenResponse {
  expiration: string;
  // eslint-disable-next-line camelcase
  link_token: string;
  // eslint-disable-next-line camelcase
  request_id: string;
  // eslint-disable-next-line camelcase
  status_code: number;
}

export interface PlaidLinkToken {
  data: PlaidLinkTokenResponse;
  requestId: string;
}

export default {
  getClientServiceInstance(): ClientService {
    return new ClientService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  getCustodianServiceInstance(): CustodianService {
    return new CustodianService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },

  getApexServiceInstance(): ApexService {
    return new ApexService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  async fetchByUserAndId(options): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getTransferMoneyRequests(options);
  },

  async fetchAllByUserAndStatus(options): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getTransferMoneyRequests(options);
  },

  async fetchUserBankAccounts(clUserId: string, accountId: string): Promise<unknown> {
    const filters = accountId ? {accountId} : {};

    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getAllBankAccountsForClUser({clUserId, ...filters});
  },

  async fetchLinkedBankAccounts(options: GetLinkedBankAccountsForClUserOptions): Promise<LinkedCustodianAccount[]> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getLinkedBankAccountsForClUser(options);
  },

  async fetchBankAccountPlaidLinkStatus(clUserId: string, accountId: string): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getBankAccountPlaidLinkStatusForClUser({clUserId, accountId});
  },

  async getAccountLimits(accountType: AccountType, year: string): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    const yearInNumber: number = Number.parseInt(year, 10);

    return service.getAccountTypeContributionLimit({
      accountType,
      year: yearInNumber,
    });
  },

  async setTransferData(options: UpdateTransferMoneyRequestOptions): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.updateMoneyTransferRequest(options);
  },

  async save(options: TransferMoneyRequestOptions): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.transferMoneyRequest(options);
  },

  async delete(options: DeleteMoneyTransferRequestOptions): Promise<void> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.deleteMoneyTransferRequest(options);
  },

  async getApexLabels(): Promise<ApexLabelResponse[]> {
    const service: ApexService = await this.getApexServiceInstance();

    const response = await service.getApexLabels({});

    return response;
  },

  async getClientDirectedContributionTypes(accountTypeId: string): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    const contributionTypes = await service.getclientDirectedAccountTypeContributionTypes({accountTypeId});

    const apexLabels = await this.getApexLabels();

    const contributionTypeMap = contributionTypes.map((contributionType) => {
      const contributionLabel = apexLabels.find((label) => label.apexLabel === contributionType);

      return {contributionType, contributionTypeText: contributionLabel.uiLabel};
    });

    return contributionTypeMap;
  },

  async updateBankAccountIdentityRequest(
    clUserId: string,
    bankAccountIdentityRequestId: string,
    status: BankAccountIdentityStatus
  ): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.updateBankAccountIdentityRequest({
      clUserId,
      bankAccountIdentityRequestId,
      status,
    });
  },

  async updateIdentityDocumentRequestDocId(
    clUserId: string,
    identityDocumentRequestId: string,
    docId: string
  ): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.updateBankAccountIdentityDocumentRequest({
      clUserId,
      identityDocumentRequestId,
      docId,
    });
  },

  async getMicroDeposit({clUserId}: GetMicroDepositOptions): Promise<unknown> {
    const custodianService: CustodianService = await this.getCustodianServiceInstance();
    const response = await custodianService.getMicroDeposit({clUserId});

    return response;
  },

  async verifyDeposit(requestBody: VerifyDepositOptions): Promise<VerifyDepositTransferResponse> {
    const custodianService: CustodianService = await this.getCustodianServiceInstance();
    const verifyDepositParams = {...requestBody};
    const response = await custodianService.verifyDeposit(verifyDepositParams);

    return response;
  },

  async verifyManualMicroDeposit({microDepositId}: VerifyMicroDepositOptions): Promise<void> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    await service.verifyMicroDeposit({microDepositId});
  },

  async associateTransferIdWithBalanceCheckAttempts({clUserId, transferId, attemptIds}): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.patchBalanceCheckAttempts({
      clUserId,
      transferId,
      attemptIds,
    });
  },

  async getLinkedBankAccounts(userId: string): Promise<unknown> {
    const response = await bffApi
      .getAxiosInstance()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get<BffResponse<any>>(`${bffApi.getBaseUrl(true)}/clients/${userId}/bank-accounts`);

    return response;
  },

  async getTransactionValidationFlag(
    transactionDate: string,
    transactionDirection: string
  ): Promise<AxiosResponse<BffResponse<TransactionValidationResponse>>> {
    const response = await bffApi
      .getAxiosInstance()
      .get<BffResponse<TransactionValidationResponse>>(
        `${bffApi.getBaseUrl(
          true
        )}/move-money/validateTransaction?transactionDate=${transactionDate}&transactionDirection=${transactionDirection}`
      );

    return response;
  },

  async cancelMicroDeposit({microDepositId, error}: CancelMicroDepositOptions): Promise<void> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.cancelMicroDeposit({
      microDepositId,
      error,
    });
  },

  async getPlaidConfig(): Promise<unknown> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await bffApi.getAxiosInstance().get<BffResponse<any>>(`${bffApi.getBaseUrl()}/v1/plaid-config`);

    return response;
  },

  async getTransferFrequencies(options: GetCustodianTransferFrequencyOptions): Promise<TransferFrequencyObject[]> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getCustodianTransferFrequency(options);
  },

  async createLinkToken(body: PlaidLinkTokenRequest): Promise<PlaidLinkTokenResponse> {
    const {data} = await bffApi
      .getAxiosInstance()
      .post<PlaidLinkTokenRequest, BffResponse<PlaidLinkToken>>(
        `${bffApi.getBaseUrl(true)}/move-money/plaid/link-tokens`,
        body
      );

    return data.data;
  },

  async getDelayedTransfers({clUserId}: DelayedTransfersOptions): Promise<DelayedTransfer[]> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getDelayedTransfers({
      clUserId,
    });
  },

  async createBankAccount(bankAccountRequest: CreateClBankAccountOptions): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.createBankAccount(bankAccountRequest);
  },

  async completePlaidItemUpdateRequest(itemUpdateRequestId: number): Promise<PlaidItemUpdate> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.patchPlaidItemUpdateRequest({id: itemUpdateRequestId, complete: true});
  },

  async updateRequestedTransfer(options: UpdateRequestedTransferRequest): Promise<UpdateRequestedTransferResponse> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.updateRequestedTransfer(options);
  },

  async deleteBankAccount(bankAccountId: number): Promise<void> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.deleteBankByBankAccountId({bankAccountId});
  },
  async getBusinessDay(options: NextBusinessDateOptions): Promise<BusinessDateResponse> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getNextBusinessDate(options);
  },
  async getCustodianAccounts(options: GetCustodianAccountsOptions): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getCustodianAccounts(options);
  },
  async linkBankAccount(options: LinkBankAccountOptions): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.linkBankAccount(options);
  },
  async getMonetaryTransactions(
    userId,
    offSet,
    limit,
    transactionCodes,
    sortField,
    sortOrder
  ): Promise<MicroserviceResponse<AccountTransactionDetails[]>> {
    const service: ClientService = await this.getClientServiceInstance();
    const safeLimit = limit < 0 ? TRANSACTIONS.DEFAULTS.LIMIT : limit;

    return service.getMonetaryTransactions({
      clUserId: userId,
      offSet,
      limit: safeLimit,
      transactionCodes,
      sortField,
      sortOrder,
    });
  },
  async getBankData(options: GetPlaidAccountOptions): Promise<GetPlaidAccountResponse> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getBankData(options);
  },
  async getAccessToken(payload: GetAccessTokenRequest): Promise<GetAccessTokenResponse> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getAccessToken(payload);
  },
  async fetchBankLinkRequestsByStatus(options: GetAllBankAccountLinkRequestsOptions): Promise<BankAccountLinkRequest[]> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getAllBankAccountLinkRequests(options);
  },
  async updateUserBankLinkRequestById(options: PatchBankAccountLinkRequestOptions): Promise<BankAccountLinkRequest> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.patchBankAccountLinkRequest(options);
  },
  async getCompletedTransactions(
    options: PostTransactionsOptions
  ): Promise<MicroserviceResponse<TransactionReportResponseOptions>> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.postCustodianTransactions(options);
  },
};
